import React from "react";
import { EventSummary, TeamAttendanceMap } from "../types";
import { ScoreBadge } from "./ScoreBadge";

interface EventListProps {
  events: EventSummary[];
  onSelect: (id: number) => void;
  selectedId: number | null;
  teamAttendance?: TeamAttendanceMap;
}

const categoryLabel = (event: EventSummary): string => {
  const cat = event.event_category || event.category || "";
  const source = event.source || "";

  if (source.startsWith("hasici") || cat === "hasici") return "Hasiči / sport";
  if (cat === "kino") return "Kino / film";
  if (cat === "divadlo") return "Divadlo";
  if (cat === "kultura") return "Kultura";
  if (cat === "sport") return "Sport";
  if (cat === "festival") return "Festival";
  if (cat === "trhy") return "Trhy";
  if (cat === "prednaska") return "Přednáška";
  if (cat === "charity") return "Charity";

  const sc = event.source_category || event.category || "";
  if (sc === "C") return "Kino / film";
  if (sc === "L") return "Kultura";
  if (sc === "M") return "Hudba";
  if (sc === "S") return "Společenské";
  if (sc === "E") return "Příroda / sport";
  if (sc === "P") return "Přednáška";
  if (sc === "T") return "Zábava";
  if (sc === "A") return "Volný čas";

  return "Akce";
};

const formatDate = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("cs-CZ", { day: "numeric", month: "short" });
};

export const EventList: React.FC<EventListProps> = ({ events, onSelect, selectedId, teamAttendance }) => {
  if (events.length === 0) {
    return (
      <div
        style={{
          borderRadius: "12px",
          padding: "2rem",
          textAlign: "center",
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <p
          style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 300,
            fontSize: "0.8125rem",
            color: "#666666",
            margin: 0,
          }}
        >
          Žádné akce neodpovídají filtru.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.375rem" }}>
      {events.map((e) => {
        const isSelected = selectedId === e.id;
        const team = teamAttendance?.[String(e.id)];
        const goingCount = team?.going ?? 0;
        return (
          <button
            key={e.id}
            onClick={() => onSelect(e.id)}
            style={{
              width: "100%",
              textAlign: "left",
              borderRadius: "10px",
              padding: "0.75rem 1rem",
              cursor: "pointer",
              transition: "border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease",
              background: isSelected
                ? "rgba(255,255,255,0.07)"
                : "rgba(255,255,255,0.04)",
              border: isSelected
                ? "1px solid rgba(255,255,255,0.35)"
                : "1px solid rgba(255,255,255,0.08)",
              boxShadow: isSelected
                ? "inset 0 0 20px rgba(255,255,255,0.03), 0 0 16px rgba(255,255,255,0.04)"
                : "none",
              outline: "none",
            }}
            onMouseEnter={(ev) => {
              if (!isSelected) {
                (ev.currentTarget as HTMLButtonElement).style.borderColor = "rgba(255,255,255,0.2)";
                (ev.currentTarget as HTMLButtonElement).style.transform = "translateY(-2px)";
                (ev.currentTarget as HTMLButtonElement).style.boxShadow =
                  "0 4px 16px rgba(0,0,0,0.4)";
              }
            }}
            onMouseLeave={(ev) => {
              if (!isSelected) {
                (ev.currentTarget as HTMLButtonElement).style.borderColor = "rgba(255,255,255,0.08)";
                (ev.currentTarget as HTMLButtonElement).style.transform = "translateY(0)";
                (ev.currentTarget as HTMLButtonElement).style.boxShadow = "none";
              }
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.75rem" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3
                  style={{
                    fontFamily: "'Syne', sans-serif",
                    fontWeight: 600,
                    fontSize: "0.8125rem",
                    color: "#fafafa",
                    margin: 0,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    letterSpacing: "-0.01em",
                  }}
                >
                  {e.title}
                </h3>
                <p
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 300,
                    fontSize: "0.75rem",
                    color: "#a1a1a1",
                    margin: "0.25rem 0 0",
                    letterSpacing: "0.01em",
                  }}
                >
                  {formatDate(e.date)} &middot; {e.city}
                </p>
                <p
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 300,
                    fontSize: "0.6875rem",
                    color: "#666666",
                    margin: "0.125rem 0 0",
                    letterSpacing: "0.01em",
                  }}
                >
                  {categoryLabel(e)}
                  {e.organizer ? ` · ${e.organizer}` : ""}
                </p>
              </div>
              <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.25rem" }}>
                {goingCount > 0 && (
                  <span
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontWeight: 400,
                      fontSize: "0.625rem",
                      letterSpacing: "0.01em",
                      color: "#4ade80",
                      background: "rgba(74, 222, 128, 0.08)",
                      border: "1px solid rgba(74, 222, 128, 0.3)",
                      borderRadius: "4px",
                      padding: "0.1rem 0.4rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {goingCount} jdou
                  </span>
                )}
                <ScoreBadge score={e.priority_score} size="sm" />
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
};
